import { delegate } from '@/lib/events';
import {
  create as createAddress,
  prepareTemplate,
  SELECTOR_SHOW_LOOKUP
} from '@/embed/address';
import { remove } from '@/lib/dom';
import { sendEvent } from './analytics';

/**
 * Handles the addition and removal of extra address
 * fields to get a long enough address history for
 * possible credit checks
 * @module embed/address_history
 */
const SELECTOR = '.js-address-history';
const SELECTOR_ADDRESS_TYPE = '.js-address-history__addressType';
const SELECTOR_ADDRESS = '.js-address-history__address';
const SELECTOR_SOURCE = '[data-address-history-source]';
const SELECTOR_RESIDENCY_FIELDS = '.js-address-history__residency';

const ATTR_ADDRESS_TYPE = 'data-address-history-type';

export const EVENT_ADDRESS_ADDED = 'didAddAddress';

const MINIMUM_LENGTH_OF_RESIDENCY = 36;

const NEXT_ADDRESS_TYPE = {
  present: 'previous1',
  previous1: 'previous2',
  previous2: 'previous3'
};
const ADDRESS_TYPE_LABEL = {
  present: 'Current address',
  previous1: 'Previous address',
  previous2: 'Second previous address',
  previous3: 'Third previous address'
};

export default function setupAddressHistories(el, client) {
  Array.from(el.querySelectorAll(SELECTOR)).forEach(el => {
    setupAddressHistory(el, client);
  });
}

function setupAddressHistory(el, client) {
  const source = prepareTemplate(el.querySelector(SELECTOR_SOURCE));

  el.addEventListener(
    'change',
    delegate(SELECTOR_RESIDENCY_FIELDS, () => {
      const addresses = Array.from(el.querySelectorAll(SELECTOR_ADDRESS));
      const lastAddress = addresses[addresses.length - 1];
      if (haveLongEnoughResidency(addresses)) {
        // If the update happened up in the list,
        // that might make some extra addresses unnecessary
        removeUnnecessaryAddresses(addresses);
      } else {
        // We need a new address, unless the last one hasn't
        // had its residency fields filled yet
        if (hasFilledResidencyFields(lastAddress)) {
          addExtraAddress(el, source, client, lastAddress);
        }
      }

      removeUnnecessaryUnfilledAddresses(addresses);
    })
  );
}

function removeUnnecessaryUnfilledAddresses(addresses) {
  const allButLastAddress = addresses.slice(0, -1);
  const lastAddress = addresses[addresses.length - 1];
  if (
    // If there's only one address, we don't have anything to remove
    allButLastAddress.length &&
    !hasFilledResidencyFields(allButLastAddress[allButLastAddress.length - 1])
  ) {
    remove(lastAddress);
    removeUnnecessaryUnfilledAddresses(allButLastAddress);
  }
}

function removeUnnecessaryAddresses(addresses) {
  const allButLastAddress = addresses.slice(0, -1);
  const lastAddress = addresses[addresses.length - 1];
  if (haveLongEnoughResidency(allButLastAddress)) {
    // We don't need the last address anymore
    remove(lastAddress);
    // But we might also not need the second to last so need to check again
    removeUnnecessaryAddresses(allButLastAddress);
  }
}

// Checks if given list of address fields tally to a long enough residency time
function haveLongEnoughResidency(addresses) {
  const residencyFields = addresses.reduce((foundFields, el) => {
    return [...foundFields, ...findResidencyFields(el)];
  }, []);
  const total = residencyFields.reduce((total, field) => {
    const value = parseInt(field.value || '0');
    if (field.name.indexOf('year') != -1) {
      return total + 12 * value;
    }
    return total + value;
  }, 0);
  return total >= MINIMUM_LENGTH_OF_RESIDENCY;
}

// Gather residency duration fields within the given element
function findResidencyFields(el) {
  return Array.from(el.querySelectorAll(SELECTOR_RESIDENCY_FIELDS));
}

function hasFilledResidencyFields(address) {
  return Array.from(address.querySelectorAll(SELECTOR_RESIDENCY_FIELDS)).some(
    field => field.value != ''
  );
}

function addExtraAddress(el, template, client, lastAddressEl) {
  const lastAddressType = lastAddressEl.getAttribute(ATTR_ADDRESS_TYPE);
  const nextType = NEXT_ADDRESS_TYPE[lastAddressType];
  if (nextType) {
    const address = createAddress(template, client, { type: nextType });
    address.querySelector(SELECTOR_ADDRESS_TYPE).textContent =
      ADDRESS_TYPE_LABEL[nextType];
    // Need to replace innerHtml because of SVG for icon
    const lookup = address.querySelector(SELECTOR_SHOW_LOOKUP);
    lookup.innerHTML = lookup.innerHTML.replace(
      'Back to address list',
      'Search again'
    );

    // Notify GA to investigate issue with extra addresses being unnecessarily added
    sendEvent(el, 'didAddAddress', nextType);

    el.appendChild(address);
    const event = new CustomEvent('didAddAddress', {
      bubbles: true,
      detail: {
        address
      }
    });
    el.dispatchEvent(event);
  }
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/embed/address_history.js